import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './CountySelector.css';
import { API_BASE } from '../config/apiConfig';

const STATES = [
  {
    id: 'indiana',
    name: 'Indiana',
    abbreviation: 'IN',
    cities: [
      {
        name: 'West Lafayette',
        counties: ['Tippecanoe County']
      },
      {
        name: 'Lafayette',
        counties: ['Tippecanoe County']
      },
      {
        name: 'Indianapolis',
        counties: ['Marion County']
      },
      {
        name: 'Fort Wayne',
        counties: ['Allen County']
      },
      {
        name: 'Gary',
        counties: ['Lake County']
      },
      {
        name: 'Evansville',
        counties: ['Vanderburgh County']
      },
      {
        name: 'South Bend',
        counties: ['St. Joseph County']
      },
      {
        name: 'Carmel',
        counties: ['Hamilton County']
      },
      {
        name: 'Bloomington',
        counties: ['Monroe County']
      },
      {
        name: 'Hammond',
        counties: ['Lake County']
      },
      {
        name: 'Fishers',
        counties: ['Hamilton County']
      },
      {
        name: 'Mishawaka',
        counties: ['St. Joseph County']
      },
      {
        name: 'Merrillville',
        counties: ['Lake County']
      },
      {
        name: 'Noblesville',
        counties: ['Hamilton County']
      },
      {
        name: 'Terre Haute',
        counties: ['Vigo County']
      },
      {
        name: 'Anderson',
        counties: ['Madison County']
      },
      {
        name: 'Elkhart',
        counties: ['Elkhart County']
      },
      {
        name: 'Kokomo',
        counties: ['Howard County']
      },
      {
        name: 'Richmond',
        counties: ['Wayne County']
      },
      {
        name: 'Columbus',
        counties: ['Bartholomew County']
      }
    ]
  }
];

const CountySelector = () => {
  const [selectedState, setSelectedState] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null); // Combined city + county
  const [latestLocation, setLatestLocation] = useState(null);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStateDropdown(false);
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchLatestLocation = async () => {
      // Get latest saved location for current user
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      const savedLocations = JSON.parse(localStorage.getItem('userSavedLocations') || '[]');
      
      console.log('Fetching latest location:', { currentUser, savedLocations });
      
      let foundLocation = null;
      
      // Priority: 1. Most recent saved location, 2. User's account location from API
      if (savedLocations.length > 0) {
        // Get the most recent location (first in array as they're sorted by timestamp)
        const savedLoc = savedLocations[0];
        console.log('Using saved location:', savedLoc);
        if (savedLoc && savedLoc.city && savedLoc.county) {
          foundLocation = savedLoc;
        }
      }
      
      // If no saved location, try API
      if (!foundLocation) {
        try {
          const response = await axios.get(`${API_BASE}/auth/me`, {
            withCredentials: true
          });
          console.log('API response:', response.data);
          
          if (response.data.success && response.data.user.city && response.data.user.county) {
            const user = response.data.user;
            foundLocation = {
              state: user.state,
              stateAbbr: user.state?.substring(0, 2) || '',
              city: user.city,
              county: user.county
            };
            console.log('Setting location from API:', foundLocation);
          }
        } catch (error) {
          console.error('Failed to fetch user location:', error);
          // Fallback to currentUser from localStorage if available
          if (currentUser && currentUser.city && currentUser.county) {
            foundLocation = {
              state: currentUser.state,
              stateAbbr: currentUser.state?.substring(0, 2) || '',
              city: currentUser.city,
              county: currentUser.county
            };
            console.log('Setting location from currentUser:', foundLocation);
          }
        }
      }
      
      if (foundLocation) {
        console.log('Setting latestLocation:', foundLocation);
        setLatestLocation(foundLocation);
      } else {
        console.log('No latest location found');
      }
    };

    fetchLatestLocation();
  }, []);

  const handleStateSelect = (state) => {
    setSelectedState(state);
    setSelectedLocation(null);
    setShowStateDropdown(false);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setShowLocationDropdown(false);
  };

  // Get all locations (city + county combinations) for selected state
  const getLocations = () => {
    if (!selectedState) return [];
    const locations = [];
    selectedState.cities.forEach(city => {
      city.counties.forEach(county => {
        locations.push({
          city: city.name,
          county: county,
          fullLabel: `${city.name}, ${county}`
        });
      });
    });
    return locations;
  };

  const handleContinue = async () => {
    if (selectedState && selectedLocation) {
      // Store selection in localStorage
      const locationData = {
        state: selectedState.name,
        stateAbbr: selectedState.abbreviation,
        city: selectedLocation.city,
        county: selectedLocation.county
      };
      localStorage.setItem('selectedCounty', JSON.stringify(locationData));
      
      // Update user location in database if logged in (using cookie)
      try {
        await axios.put(
          `${API_BASE}/auth/location`,
          {
            state: locationData.state,
            city: locationData.city,
            county: locationData.county
          },
          { withCredentials: true }
        );
      } catch (error) {
        console.error('Failed to update location:', error);
      }
      
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      navigate(currentUser?.role === 'admin' ? '/admin' : '/start');
    }
  };

  const handleUseLatestLocation = async () => {
    if (!latestLocation) return;

    // Store location
    localStorage.setItem('selectedCounty', JSON.stringify(latestLocation));
    
    // Update user location in database if logged in (using cookie)
    try {
      await axios.put(
        `${API_BASE}/auth/location`,
        {
          state: latestLocation.state,
          city: latestLocation.city,
          county: latestLocation.county
        },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Failed to update location:', error);
    }
    
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
    navigate(currentUser?.role === 'admin' ? '/admin' : '/start');
  };

  return (
    <div className="county-selector-container">
      <div className="county-selector-card">
        <div className="selector-header">
          <h1>Select Location</h1>
          <p>Choose your state and county/city</p>
        </div>

        {/* Dropdown Fields */}
        <div className="selector-content" ref={dropdownRef}>
          <div className="dropdown-field-wrapper">
            <label className="dropdown-label">Select a State</label>
            <div className="dropdown-select-wrapper">
              <div
                className="dropdown-select"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowStateDropdown(!showStateDropdown);
                  setShowLocationDropdown(false);
                }}
              >
                <span className="dropdown-select-text">
                  {selectedState ? `${selectedState.name} (${selectedState.abbreviation})` : 'Select a State'}
                </span>
                <span className="dropdown-chevron">▼</span>
              </div>
              {showStateDropdown && (
                <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                  {STATES.map((state) => (
                    <div
                      key={state.id}
                      className="dropdown-item"
                      onClick={() => handleStateSelect(state)}
                    >
                      {state.name} ({state.abbreviation})
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="dropdown-field-wrapper">
            <label className="dropdown-label">Select County/City</label>
            <div className="dropdown-select-wrapper">
              <div
                className="dropdown-select"
                onClick={(e) => {
                  e.stopPropagation();
                  if (selectedState) {
                    setShowLocationDropdown(!showLocationDropdown);
                    setShowStateDropdown(false);
                  }
                }}
                style={{ 
                  opacity: selectedState ? 1 : 0.5,
                  cursor: selectedState ? 'pointer' : 'not-allowed'
                }}
              >
                <span className="dropdown-select-text">
                  {selectedLocation 
                    ? selectedLocation.fullLabel
                    : 'Select County/City'}
                </span>
                <span className="dropdown-chevron">▼</span>
              </div>
              {showLocationDropdown && selectedState && (
                <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                  {getLocations().map((location, index) => (
                    <div
                      key={`${location.city}-${location.county}-${index}`}
                      className="dropdown-item"
                      onClick={() => handleLocationSelect(location)}
                    >
                      {location.fullLabel}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="continue-button-slot">
            {selectedState && selectedLocation && (
              <button
                className="continue-button"
                onClick={handleContinue}
              >
                Continue to Dashboard →
              </button>
            )}
          </div>
        </div>

        {/* Show "Login to latest login" button at the bottom if available */}
        {latestLocation && latestLocation.city && latestLocation.county && (
          <div className="latest-login-section">
            <div className="latest-login-divider">
              <span>OR</span>
            </div>
            <button
              className="latest-login-button"
              onClick={handleUseLatestLocation}
            >
              <span className="latest-login-icon" aria-hidden="true"></span>
              <span className="latest-login-text">
                Login to Latest Location
              </span>
              <span className="latest-login-location">
                {latestLocation.city}, {latestLocation.county}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CountySelector;
